import { Button } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { Link } from "react-router-dom";

// Form validation rules
const validateForm = (
  email: string,
  password: string,
  confirmPassword: string,
  fullName: string,
  termsAccepted: boolean,
  position: string
) => {
  const errors: Record<string, string> = {};

  if (!fullName) {
    errors.fullName = "Full name is required";
  } else if (fullName.length < 2) {
    errors.fullName = "Full name must be at least 2 characters long";
  }

  if (!position) {
    errors.position = "Your role is required";
  }

  if (!email) {
    errors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(email)) {
    errors.email = "Please enter a valid email address";
  }

  if (!password) {
    errors.password = "Password is required";
  } else if (password.length < 6) {
    errors.password = "Password must be at least 6 characters long";
  }

  if (!confirmPassword) {
    errors.confirmPassword = "Please confirm your password";
  } else if (password !== confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }

  if (!termsAccepted) {
    errors.terms = "You must accept the Terms and Conditions to continue";
  }

  return errors;
};

// Signup form component
const SignupForm = ({
  email,
  setEmail,
  emailLocked = false,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  fullName,
  setFullName,
  position,
  setPosition,
  onSubmit,
  loading,
  error,
}) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm(
      email,
      password,
      confirmPassword,
      fullName,
      termsAccepted,
      position
    );
    setValidationErrors(errors);

    // If no errors, submit form
    if (Object.keys(errors).length === 0) {
      onSubmit(e);
    }
  };

  return (
    <>
      <h1 className={`text-3xl ${theme.text.primary} mb-6 font-petrona`}>
        Create your Account
      </h1>

      {/* Error message */}
      {error && (
        <div className="mb-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-1"
              htmlFor="full-name"
            >
              Full Name
            </label>
            <input
              id="full-name"
              className={`form-input w-full ${
                validationErrors.fullName ? "border-red-300" : ""
              }`}
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
            {validationErrors.fullName && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.fullName}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              className={`form-input w-full ${
                validationErrors.email ? "border-red-300" : ""
              } ${
                emailLocked
                  ? "bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  : ""
              }`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={emailLocked}
              aria-readonly={emailLocked}
              required
            />
            {emailLocked && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Your invitation was sent to this address, so your account has to
                use it.
              </p>
            )}
            {validationErrors.email && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.email}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="role">
              Your Role
            </label>
            <input
              id="role"
              className={`form-input w-full ${
                validationErrors.role ? "border-red-300" : ""
              }`}
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              required
            />
            {validationErrors.role && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.role}
              </p>
            )}
          </div>
          <div>
            <label
              className="block text-sm font-medium mb-1"
              htmlFor="password"
            >
              Password
            </label>
            <input
              id="password"
              className={`form-input w-full ${
                validationErrors.password ? "border-red-300" : ""
              }`}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            {validationErrors.password && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.password}
              </p>
            )}
          </div>
          <div>
            <label
              className="block text-sm font-medium mb-1"
              htmlFor="confirm-password"
            >
              Confirm Password
            </label>
            <input
              id="confirm-password"
              className={`form-input w-full ${
                validationErrors.confirmPassword ? "border-red-300" : ""
              }`}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            {validationErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.confirmPassword}
              </p>
            )}
          </div>
        </div>
        <div className="mt-6">
          <div className="flex items-start mb-4">
            <div className="flex items-center h-5">
              <input
                id="terms"
                type="checkbox"
                className={`form-checkbox ${
                  validationErrors.terms ? "border-red-300" : ""
                }`}
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className={`${theme.text.primary}`}>
                I agree to the{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${theme.text.link} hover:underline`}
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${theme.text.link} hover:underline`}
                >
                  Terms &amp; Conditions
                </a>
              </label>
              {validationErrors.terms && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.terms}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Creating Account..." : "Sign Up"}
            </Button>
          </div>
        </div>
      </form>

      {/* Sign in link */}
      <div className="pt-5 mt-6 border-t border-gray-200">
        <div className="text-sm">
          Have an account?{" "}
          <Link className={`font-medium ${theme.text.link}`} to="/signin">
            Sign In
          </Link>
        </div>
      </div>
    </>
  );
};

export default SignupForm;
